/**
 * Copyright (c) 2024 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Simon Paidla <s.paidla@techdivision.com>
 */

import React from 'react';
import { Box, CircularProgress } from '@mui/material';
import PropTypes from 'prop-types';
import { OccupancyFrontendError } from './OccupancyFrontendError';
import { OccupancyBackendError } from './OccupancyBackendError';
import { useOccupancySpots } from './useOccupancySpots';

/**
 * OccupancySpots Component
 * @returns {Element}
 * @constructor
 */
export function OccupancySpots(props) {
  const { studioId, isNeosBackend } = props;
  const { isLoading, isError, studioOccupancy } = useOccupancySpots(studioId);

  /**
     * Render occupancy spot and handle special cases
     * 1: IsLoading => Show progress spinner when request is loading
     * 2: No studio id and user is in backend => Show backend error
     * 3: IsError => Show frontend error when something went wrong
     * 4: Everything is ok => Show occupancy data
     * @returns {Element}
     */
  const renderOccupancy = () => {
    // Show progress spinner when request is loading
    if (isLoading) {
      return (
        <CircularProgress
          className="occupancy-spots__progress"
          size="2.5rem"
        />
      );
    }

    // No studio id is set and user is in neos backend
    if (!studioId && isNeosBackend) {
      return <OccupancyBackendError />;
    }

    // Show frontend error when something went wrong
    if (isError) {
      return <OccupancyFrontendError />;
    }

    // Return occupancy data
    return (
      <Box className="occupancy-spots">
        <Box className="occupancy-spots__bar" style={{ width: `${studioOccupancy}%` }}> &nbsp;</Box>
        <Box className="occupancy-spots__text">
          {isLoading ? (
            <CircularProgress
              className="occupancy-spots__progress"
              size="1.2rem"
            />
          ) : `Belegt: ${studioOccupancy}%`}
        </Box>
      </Box>
    );
  };

  return (
    <>
      {renderOccupancy()}
    </>
  );
}

OccupancySpots.propTypes = {
  studioId: PropTypes.string, isNeosBackend: PropTypes.string,
};

OccupancySpots.defaultProps = {
  studioId: null, isNeosBackend: null,
};
